/**
 * SubscriptionManager — modal for managing status page notification subscriptions.
 *
 * Tabs:
 *   Email   — email input + Subscribe button, shows confirmation message
 *   Webhook — URL input + Test button + component/severity filter checkboxes
 *   RSS     — displays feed URL with copy-to-clipboard button
 */

import { useState, useCallback } from 'react';
import type { StatusComponent, IncidentSeverity } from '../../api/status.js';
import { subscriptionApi } from '../../api/status.js';

// ── Types ─────────────────────────────────────────────────────────────────────

type Channel = 'email' | 'webhook' | 'rss';

interface SubscriptionManagerProps {
  isOpen: boolean;
  onClose: () => void;
  components?: StatusComponent[];
}

// ── Severity filter options ───────────────────────────────────────────────────

const SEVERITIES: { value: IncidentSeverity; label: string; color: string }[] = [
  { value: 'minor',    label: 'Minor',    color: 'bg-neutral-100 text-neutral-700' },
  { value: 'major',    label: 'Major',    color: 'bg-amber-100 text-amber-800'   },
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-800'       },
];

// ── Channel tab ───────────────────────────────────────────────────────────────

function ChannelTab({
  channel,
  active,
  onClick,
  children,
}: {
  channel: Channel;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-blue-500 text-blue-700'
          : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
      }`}
    >
      {children}
    </button>
  );
}

// ── Email tab content ─────────────────────────────────────────────────────────

function EmailTab({ components }: { components?: StatusComponent[] }) {
  const [email, setEmail] = useState('');
  const [selectedComponents, setSelectedComponents] = useState<string[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const toggleComponent = (id: string) => {
    setSelectedComponents((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email) return;

      setStatus('loading');
      setMessage('');

      try {
        await subscriptionApi.createEmailSubscription({
          email,
          component_ids: selectedComponents.length > 0 ? selectedComponents : null,
        });

        setStatus('success');
        setMessage('Confirmation email sent. Please check your inbox and click the link to confirm your subscription.');
        setEmail('');
        setSelectedComponents([]);
      } catch (err: unknown) {
        setStatus('error');
        const errMsg = err instanceof Error ? err.message : 'Failed to subscribe';
        if (errMsg.includes('EMAIL_ALREADY_SUBSCRIBED') || errMsg.includes('409')) {
          setMessage('This email is already subscribed to status notifications.');
        } else if (errMsg.includes('429')) {
          setMessage('Too many subscription attempts. Please try again in an hour.');
        } else {
          setMessage(errMsg);
        }
      }
    },
    [email, selectedComponents],
  );

  return (
    <div className="space-y-4">
      {status === 'success' ? (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-emerald-800">{message}</p>
          </div>
          <button
            type="button"
            onClick={() => { setStatus('idle'); setMessage(''); }}
            className="mt-3 text-sm text-emerald-700 underline hover:no-underline"
          >
            Subscribe another email
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="subscription-email" className="block text-sm font-medium text-neutral-700 mb-1">
              Email address
            </label>
            <input
              id="subscription-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md shadow-sm
                         focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent
                         disabled:bg-neutral-50 disabled:text-neutral-500"
              disabled={status === 'loading'}
            />
          </div>

          {components && components.length > 0 && (
            <div>
              <p className="text-sm font-medium text-neutral-700 mb-2">
                Notify me about (leave unchecked for all):
              </p>
              <div className="max-h-40 overflow-y-auto border border-neutral-200 rounded-md divide-y divide-neutral-100">
                {components.map((comp) => (
                  <label
                    key={comp.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-neutral-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedComponents.includes(comp.id)}
                      onChange={() => toggleComponent(comp.id)}
                      className="h-4 w-4 text-blue-600 border-neutral-300 rounded"
                    />
                    <span className="text-sm text-neutral-700">{comp.name}</span>
                    <span className="text-xs text-neutral-400 ml-auto">{comp.group}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {status === 'error' && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {message}
            </p>
          )}

          <button
            type="submit"
            disabled={status === 'loading' || !email}
            className="w-full py-2 px-4 bg-blue-600 text-white text-sm font-medium rounded-md
                       hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500
                       disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {status === 'loading' ? 'Subscribing…' : 'Subscribe'}
          </button>
        </form>
      )}
    </div>
  );
}

// ── Webhook tab content ───────────────────────────────────────────────────────

function WebhookTab({ components }: { components?: StatusComponent[] }) {
  const [webhookUrl, setWebhookUrl] = useState('');
  const [selectedComponents, setSelectedComponents] = useState<string[]>([]);
  const [selectedSeverities, setSelectedSeverities] = useState<IncidentSeverity[]>([]);
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [testMessage, setTestMessage] = useState('');
  const [subStatus, setSubStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [subMessage, setSubMessage] = useState('');

  const toggleComponent = (id: string) => {
    setSelectedComponents((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  };

  const toggleSeverity = (s: IncidentSeverity) => {
    setSelectedSeverities((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  };

  const handleTest = useCallback(async () => {
    if (!webhookUrl) return;

    setTestStatus('testing');
    setTestMessage('');

    try {
      // Send a test by creating a subscription (webhook test happens server-side)
      // We test by calling a dedicated test endpoint, or just attempt subscription
      const url = new URL(webhookUrl); // validate URL client-side
      void url; // suppress unused warning

      // Make a raw fetch to test the URL structure
      setTestStatus('success');
      setTestMessage('URL format is valid. The server will test your webhook on subscription.');
    } catch {
      setTestStatus('error');
      setTestMessage('Invalid URL format. Please enter a valid HTTPS URL.');
    }
  }, [webhookUrl]);

  const handleSubscribe = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!webhookUrl) return;

      setSubStatus('loading');
      setSubMessage('');
      setTestStatus('idle');
      setTestMessage('');

      try {
        await subscriptionApi.createWebhookSubscription({
          webhook_url: webhookUrl,
          component_ids: selectedComponents.length > 0 ? selectedComponents : null,
          severity_filter: selectedSeverities.length > 0 ? selectedSeverities : null,
        });

        setSubStatus('success');
        setTestStatus('success');
        setSubMessage('Webhook subscription activated. Your endpoint will receive incident notifications.');
      } catch (err: unknown) {
        setSubStatus('error');
        setTestStatus('error');
        const errMsg = err instanceof Error ? err.message : 'Failed to subscribe';
        if (errMsg.includes('WEBHOOK_UNREACHABLE') || errMsg.includes('422')) {
          setSubMessage('Webhook URL is unreachable. Make sure your endpoint returns a 2xx response within 5 seconds.');
          setTestMessage('Endpoint test failed — not reachable');
        } else {
          setSubMessage(errMsg);
          setTestMessage('Test failed');
        }
      }
    },
    [webhookUrl, selectedComponents, selectedSeverities],
  );

  return (
    <div className="space-y-4">
      {subStatus === 'success' ? (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-emerald-800">{subMessage}</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setSubStatus('idle'); setSubMessage('');
              setTestStatus('idle'); setTestMessage('');
              setWebhookUrl('');
            }}
            className="mt-3 text-sm text-emerald-700 underline hover:no-underline"
          >
            Add another webhook
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubscribe} className="space-y-4">
          <div>
            <label htmlFor="webhook-url" className="block text-sm font-medium text-neutral-700 mb-1">
              Webhook URL
            </label>
            <div className="flex gap-2">
              <input
                id="webhook-url"
                type="url"
                required
                value={webhookUrl}
                onChange={(e) => {
                  setWebhookUrl(e.target.value);
                  setTestStatus('idle');
                  setTestMessage('');
                }}
                placeholder="https://your-service.com/webhook"
                className="flex-1 px-3 py-2 text-sm border border-neutral-300 rounded-md shadow-sm
                           focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent
                           disabled:bg-neutral-50"
                disabled={subStatus === 'loading'}
              />
              <button
                type="button"
                onClick={handleTest}
                disabled={!webhookUrl || testStatus === 'testing' || subStatus === 'loading'}
                className="px-3 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300
                           rounded-md hover:bg-neutral-50 focus:outline-none focus:ring-1 focus:ring-blue-500
                           disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                title="Validate URL format"
              >
                {testStatus === 'testing' ? 'Testing…' : 'Test'}
              </button>
            </div>

            {/* Test status indicator */}
            {testStatus === 'success' && (
              <div className="mt-1.5 flex items-center gap-1.5 text-emerald-600">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-xs">{testMessage || 'URL looks valid'}</span>
              </div>
            )}
            {testStatus === 'error' && (
              <div className="mt-1.5 flex items-center gap-2">
                <svg className="w-4 h-4 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="text-xs text-red-600">{testMessage}</span>
                <button
                  type="button"
                  onClick={() => { setTestStatus('idle'); setTestMessage(''); }}
                  className="text-xs text-red-500 underline ml-1"
                >
                  Retry
                </button>
              </div>
            )}
          </div>

          {/* Component filter */}
          {components && components.length > 0 && (
            <div>
              <p className="text-sm font-medium text-neutral-700 mb-2">
                Notify for components (leave unchecked for all):
              </p>
              <div className="max-h-32 overflow-y-auto border border-neutral-200 rounded-md divide-y divide-neutral-100">
                {components.map((comp) => (
                  <label
                    key={comp.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-neutral-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedComponents.includes(comp.id)}
                      onChange={() => toggleComponent(comp.id)}
                      className="h-4 w-4 text-blue-600 border-neutral-300 rounded"
                    />
                    <span className="text-sm text-neutral-700">{comp.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Severity filter */}
          <div>
            <p className="text-sm font-medium text-neutral-700 mb-2">
              Severity filter (leave unchecked for all):
            </p>
            <div className="flex gap-2">
              {SEVERITIES.map(({ value, label, color }) => (
                <label
                  key={value}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium cursor-pointer
                    ${selectedSeverities.includes(value) ? color : 'bg-neutral-100 text-neutral-500'}
                    hover:opacity-80 transition-opacity`}
                >
                  <input
                    type="checkbox"
                    checked={selectedSeverities.includes(value)}
                    onChange={() => toggleSeverity(value)}
                    className="sr-only"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {subStatus === 'error' && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {subMessage}
            </p>
          )}

          <button
            type="submit"
            disabled={subStatus === 'loading' || !webhookUrl}
            className="w-full py-2 px-4 bg-blue-600 text-white text-sm font-medium rounded-md
                       hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500
                       disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {subStatus === 'loading' ? 'Subscribing…' : 'Subscribe via Webhook'}
          </button>
        </form>
      )}
    </div>
  );
}

// ── RSS tab content ───────────────────────────────────────────────────────────

function RssTab() {
  const feedUrl = `${window.location.origin}/api/status/feed.rss`;
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(feedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2_000);
    } catch {
      // Fallback for browsers without clipboard API
      const input = document.createElement('input');
      input.value = feedUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2_000);
    }
  }, [feedUrl]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-600">
        Subscribe to the RSS 2.0 feed to receive incident updates in your feed reader.
        The feed is updated in real time and includes the last 50 incidents.
      </p>

      <div>
        <p className="text-sm font-medium text-neutral-700 mb-2">Feed URL</p>
        <div className="flex gap-2 items-stretch">
          <input
            type="text"
            readOnly
            value={feedUrl}
            className="flex-1 px-3 py-2 text-sm bg-neutral-50 border border-neutral-300 rounded-md
                       text-neutral-700 font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button
            type="button"
            onClick={handleCopy}
            aria-label={copied ? 'Copied!' : 'Copy feed URL to clipboard'}
            className={`px-3 py-2 text-sm font-medium rounded-md border transition-all
              focus:outline-none focus:ring-1 focus:ring-blue-500 ${
              copied
                ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                : 'bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50'
            }`}
          >
            {copied ? (
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Copied
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="rounded-md bg-blue-50 border border-blue-100 p-3">
        <p className="text-xs text-blue-700">
          <strong>RSS readers:</strong> Paste the URL above into your RSS reader app (Feedly, Inoreader, NetNewsWire, etc.) to subscribe.
        </p>
      </div>

      <a
        href={feedUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
        Open feed in browser
      </a>
    </div>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────

export function SubscriptionManager({
  isOpen,
  onClose,
  components,
}: SubscriptionManagerProps) {
  const [activeTab, setActiveTab] = useState<Channel>('email');

  if (!isOpen) return null;

  return (
    /* Overlay */
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="subscription-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal panel */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <h2 id="subscription-modal-title" className="text-base font-semibold text-neutral-900">
            Subscribe to Status Notifications
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded-md text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100
                       focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div
          role="tablist"
          className="flex border-b border-neutral-200 px-4 bg-neutral-50"
          aria-label="Notification channel"
        >
          <ChannelTab channel="email" active={activeTab === 'email'} onClick={() => setActiveTab('email')}>
            <span className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Email
            </span>
          </ChannelTab>
          <ChannelTab channel="webhook" active={activeTab === 'webhook'} onClick={() => setActiveTab('webhook')}>
            <span className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Webhook
            </span>
          </ChannelTab>
          <ChannelTab channel="rss" active={activeTab === 'rss'} onClick={() => setActiveTab('rss')}>
            <span className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M6 5c7.18 0 13 5.82 13 13M6 11a7 7 0 017 7M6 17a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
              RSS
            </span>
          </ChannelTab>
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'email' && <EmailTab components={components} />}
          {activeTab === 'webhook' && <WebhookTab components={components} />}
          {activeTab === 'rss' && <RssTab />}
        </div>
      </div>
    </div>
  );
}

export default SubscriptionManager;
